import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { GiSpades } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Pagination from "../components/Pagination";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
const baseUrl = config.baseURL;
const locations = [
  "sidebar",
  "navbar",
  "main_top",
  "main_bottom",
  "footer",
  "custom",
];

const ZoneManager = () => {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtersInput, setFiltersInput] = useState({
    name: "",
    description: "",
    width: "",
    height: "",
    max_ads: "",
    location: "",
    page_name: "",
    order_index: "",
  });
  const [filtersApplied, setFiltersApplied] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemPerPage = 10;
  const [totalItems, setTotalItems] = useState(0);

  const token = localStorage.getItem("token");

  const buildParams = (filtersData, page = 1) => {
    const params = { page, limit: itemPerPage };
    Object.entries(filtersData).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    return params;
  };

  const fetchZones = async (page = 1, filtersData = filtersApplied) => {
    setLoading(true);
    try {
      const params = buildParams(filtersData, page);
      const query = new URLSearchParams(params).toString();

      const res = await axios.get(`${baseUrl}/api/zone?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("params zone", params);
      console.log("query zone", query);
      console.log("res zone", res.data);
      if (res.data.length === 0) {
        setError("No zones.");
      }
      setZones(res.data.data || res.data);
      setTotalItems(
        res.data.pagination?.totalItems || (res.data.data || res.data).length
      );
      setCurrentPage(res.data.pagination?.currentPage || page);
    } catch (err) {
      console.error("Error fetching zones:", err);
      setError("Failed to fetch zones.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones(currentPage);
    // eslint-disable-next-line
  }, [currentPage, filtersApplied]);

  const applyFilter = () => {
    const newFilters = {};
    Object.entries(filtersInput).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        newFilters[key] = value;
      }
    });
    setFiltersApplied(newFilters);
    setCurrentPage(1);
    fetchZones(1, newFilters);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchZones(page);
  };

  const handleCreateZone = () => {
    navigate("/dashboard/Zones/create");
  };
  const handleDelete = async (id) => {
    // Ask for confirmation before deleting
    const confirmed = window.confirm(
      "Are you sure you want to delete this zone?"
    );
    if (!confirmed) return;

    try {
      // Call your API to delete the zone
      await axios.delete(`${baseUrl}/api/zone/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Show success message
      toast.success("Zone deleted successfully!");

      // Refresh the current page of zones
      fetchZones(currentPage);
    } catch (err) {
      console.error("Failed to delete zone:", err);
      toast.error(
        err.response?.data?.error || "Failed to delete zone. Please try again."
      );
    }
  };

  return (
    <Box sx={{ p: 4, fontFamily: "Poppins, sans-serif" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <GiSpades color="#EA6A12" size={30} /> Ad Zones
        </Typography>
        <Button
          variant="contained"
          onClick={handleCreateZone}
          sx={{
            backgroundColor: "#2E4053",
            "&:hover": { backgroundColor: "#c7570f" },
            fontWeight: "bold",
            textTransform: "none",
          }}
        >
          Create Zone
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <TextField
          label="Name"
          size="small"
          value={filtersInput.name}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, name: e.target.value })
          }
        />
        <TextField
          label="Description"
          size="small"
          value={filtersInput.description}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, description: e.target.value })
          }
        />
        <TextField
          label="Width"
          type="number"
          size="small"
          value={filtersInput.width}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, width: e.target.value })
          }
        />
        <TextField
          label="Height"
          type="number"
          size="small"
          value={filtersInput.height}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, height: e.target.value })
          }
        />
        <TextField
          label="Max Ads"
          type="number"
          size="small"
          value={filtersInput.max_ads}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, max_ads: e.target.value })
          }
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={filtersInput.location}
            onChange={(e) =>
              setFiltersInput({ ...filtersInput, location: e.target.value })
            }
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Page name"
          size="small"
          value={filtersInput.page_name}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, page_name: e.target.value })
          }
        />
        <TextField
          label="Order Index"
          type="number"
          size="small"
          value={filtersInput.order_index}
          onChange={(e) =>
            setFiltersInput({ ...filtersInput, order_index: e.target.value })
          }
        />
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#2E4053",
            "&:hover": { backgroundColor: "#c7570f" },
            fontWeight: "bold",
            textTransform: "none",
          }}
          onClick={applyFilter}
        >
          Apply Filter
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: "#2E4053",
            color: "#2E4053",
            "&:hover": { borderColor: "#c7570f", color: "#c7570f" },
            fontWeight: "bold",
            textTransform: "none",
          }}
          onClick={() => {
            setFiltersInput({
              name: "",
              description: "",
              width: "",
              height: "",
              max_ads: "",
              location: "",
              page_name: "",
              order_index: "",
            });
            setFiltersApplied({});
            fetchZones(1);
          }}
        >
          Remove Filter
        </Button>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : zones.length === 0 ? (
        <Typography>No zones found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Width</TableCell>
                <TableCell>Height</TableCell>
                <TableCell>Max Ads</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Page Name</TableCell>
                <TableCell>Order Index</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zones.map((zone) => (
                <TableRow key={zone.id}>
                  <TableCell>{zone.id}</TableCell>
                  <TableCell>{zone.name}</TableCell>
                  <TableCell>{zone.description || "-"}</TableCell>
                  <TableCell>{zone.width || "-"}</TableCell>
                  <TableCell>{zone.height || "-"}</TableCell>
                  <TableCell>{zone.max_ads}</TableCell>
                  <TableCell>{zone.location}</TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {Array.isArray(zone.page_name) &&
                      zone.page_name.length > 0 ? (
                        zone.page_name.map((page) => (
                          <Chip
                            key={page}
                            label={page.charAt(0).toUpperCase() + page.slice(1)}
                            size="small"
                            color="primary"
                          />
                        ))
                      ) : (
                        <Chip label="-" size="small" color="default" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{zone.order_index}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Edit
                        sx={{ cursor: "pointer", color: "#1976d2" }}
                        onClick={() =>
                          navigate(`/dashboard/Zones/edit/${zone.id}`)
                        }
                      />
                      <Delete
                        sx={{ cursor: "pointer", color: "#d32f2f" }}
                        onClick={() => handleDelete(zone.id)}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {totalItems > itemPerPage && (
        <Pagination
          totalItem={totalItems}
          itemPerPage={itemPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          fetchItems={fetchZones}
        />
      )}
    </Box>
  );
};

export default ZoneManager;
